import React from 'react';
import {Collapse, Descriptions, Tag, Typography} from 'antd';
import {AgentWorkflowStep} from '../../hooks/useAgentWebSocket';

const {Text} = Typography;

interface AgentDebugNodeDetailProps {
    step: AgentWorkflowStep | null;
    visible: boolean;
}

const formatJson = (jsonStr: string | undefined): string => {
    if (!jsonStr) return '';
    try {
        return JSON.stringify(JSON.parse(jsonStr), null, 2);
    } catch {
        return jsonStr;
    }
};

const truncateText = (text: string | undefined, maxLen: number): string => {
    if (!text) return '';
    return text.length > maxLen ? text.substring(0, maxLen) + '...' : text;
};

const getStatusColor = (step: AgentWorkflowStep): string => {
    const fn = step.functionName || '';
    if (fn === '__agent_failed__' || step.stepType === 'error') return 'red';
    if (fn === 'submit_final_output') return 'green';
    return 'blue';
};

const getStatusLabel = (step: AgentWorkflowStep): string => {
    const fn = step.functionName || '';
    if (fn === 'submit_final_output') return '最终决策';
    if (fn === '__agent_failed__') return '失败';
    if (step.stepType === 'tool_call_debug_blocked') return '调试拦截';
    if (step.stepType === 'tool_call') return '工具调用';
    return step.stepType || step.step || fn || '未知';
};

const AgentDebugNodeDetail: React.FC<AgentDebugNodeDetailProps> = ({step, visible}) => {
    if (!visible || !step) return null;

    const funcName = step.functionName || '-';
    const stepType = step.stepType || step.type || '-';
    const processingTimeMs = step.processingTimeMs;
    const hasSessionId = step.sessionId && step.sessionId.length > 0;
    const hasDecisionId = step.decisionId && step.decisionId.length > 0;

    return (
        <div style={{
            marginTop: 12,
            padding: 12,
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 8,
            background: 'rgba(255,255,255,0.02)'
        }}>
            <div style={{marginBottom: 8}}>
                <Text strong style={{color: 'rgba(255,255,255,0.92)', fontSize: 13}}>
                    {'>'} 节点详情
                </Text>
                <Tag color={getStatusColor(step)} style={{marginLeft: 8}}>
                    {getStatusLabel(step)}
                </Tag>
            </div>

            <Descriptions column={2} size="small" style={{marginBottom: 8}}>
                <Descriptions.Item label="函数名">
                    <Text code style={{fontSize: 12}}>{funcName}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="类型">
                    <Text style={{fontSize: 12}}>{stepType}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="摘要" span={2}>
                    <Text style={{fontSize: 12, color: 'rgba(255,255,255,0.7)'}}>
                        {truncateText(step.summary, 200) || '-'}
                    </Text>
                </Descriptions.Item>
                {(hasSessionId || hasDecisionId) && (
                    <Descriptions.Item label="会话ID">
                        <Text style={{fontSize: 12, fontFamily: 'monospace'}} copyable>
                            {hasSessionId ? step.sessionId : '-'}
                        </Text>
                    </Descriptions.Item>
                )}
                {(hasSessionId || hasDecisionId) && (
                    <Descriptions.Item label="决策ID">
                        <Text style={{fontSize: 12, fontFamily: 'monospace'}} copyable>
                            {hasDecisionId ? step.decisionId : '-'}
                        </Text>
                    </Descriptions.Item>
                )}
                {processingTimeMs != null && (
                    <Descriptions.Item label="耗时">
                        <Text style={{fontSize: 12, fontFamily: 'monospace'}}>
                            {processingTimeMs >= 1000
                                ? `${(processingTimeMs / 1000).toFixed(1)}s`
                                : `${processingTimeMs}ms`}
                        </Text>
                    </Descriptions.Item>
                )}
                <Descriptions.Item label="轮次">
                    <Text style={{fontSize: 12}}>{step.turn != null ? `第 ${step.turn} 轮` : '-'}</Text>
                </Descriptions.Item>
            </Descriptions>

            <Collapse
                size="small"
                ghost
                items={[
                    {
                        key: 'arguments',
                        label: <Text style={{fontSize: 12, color: 'rgba(255,255,255,0.75)'}}>输入参数</Text>,
                        children: (
                            <pre style={{
                                background: 'var(--pt-bg-panel)',
                                color: 'var(--pt-text-primary)',
                                padding: 12,
                                borderRadius: 6,
                                fontSize: 11,
                                fontFamily: 'IBM Plex Mono, monospace',
                                overflow: 'auto',
                                maxHeight: 300,
                                border: '1px solid rgba(255,255,255,0.07)',
                                whiteSpace: 'pre-wrap',
                                margin: 0
                            }}>
                                {formatJson(step.arguments) || '无参数'}
                            </pre>
                        )
                    },
                    {
                        key: 'result',
                        label: <Text style={{fontSize: 12, color: 'rgba(255,255,255,0.75)'}}>输出结果</Text>,
                        children: (
                            <pre style={{
                                background: 'var(--pt-bg-panel)',
                                color: 'var(--pt-text-primary)',
                                padding: 12,
                                borderRadius: 6,
                                fontSize: 11,
                                fontFamily: 'IBM Plex Mono, monospace',
                                overflow: 'auto',
                                maxHeight: 300,
                                border: '1px solid rgba(255,255,255,0.07)',
                                whiteSpace: 'pre-wrap',
                                margin: 0
                            }}>
                                {formatJson(step.result) || '无输出'}
                            </pre>
                        )
                    }
                ]}
            />
        </div>
    );
};

export default AgentDebugNodeDetail;
